import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTour } from '../context/TourContext';
import './IconLegendPopup.css';
import '../list/List.css';
import '../input/Checkbox.css';
import '../add-on/AddOnChips.css';
import EventStatus from '../status/EventStatus';
import Icon from '../home/Icon';
import WaitlistStatusIcon from '../event/WaitlistStatusIcon';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import {
  COLOR_KEY_LABELS, GUEST_SYSTEM_KEY, normalizeParticipantGroups, ROLE_ORDER, roleColorKey, roleLabel,
} from '../../util/participantGroups';

const _now = Date.now();

// One add-on chip as in the lists (AddOnChips classes, compact; warning = exhausted counter).
function AddOnChipSample({ label, warning = false }) {
  return (
    <span className="addOnChips isCompact legend-addon-chip">
      <span className={`addOnChip${warning ? ' isWarning' : ''}`}>{label}</span>
    </span>
  );
}

const NAV_SECTIONS = [
  {
    title: 'Navigation',
    items: [
      {
        icon: 'home',
        label: 'Home',
        description: 'Startseite mit nächstem Spieltag.',
      },
      {
        icon: 'event',
        label: 'Kalender',
        description: 'Liste aller Spieltage.',
      },
      {
        icon: 'inbox',
        label: 'Registrierungen',
        description: 'Eingegangene Anmeldungen (Formular und persönlicher Link).',
      },
      {
        icon: 'person',
        label: 'Spielerliste',
        description: 'Liste aller Spieler.',
      },
      {
        icon: 'query_stats',
        label: 'Statistiken',
        description: 'Auswertungen.',
      },
      {
        icon: 'more_horiz',
        label: 'Mehr',
        description: 'Einstellungen, Icon-Legende, Rundtour, Logout.',
      },
    ],
  },
  {
    title: 'Mehr',
    items: [
      {
        icon: 'dark_mode',
        label: 'Dark Mode / Light Mode',
        description: 'Theme umschalten. Wird im Browser gespeichert.',
      },
      {
        icon: 'lock',
        label: 'Passwort ändern',
        description: 'Eigenes Login-Passwort aktualisieren.',
      },
      {
        icon: 'map',
        label: 'Rundtour starten',
        description: 'Auf den unterstützten Seiten (Home, Spieltage, Spieltag-Ansicht) kannst du die geführte Tour jederzeit erneut starten.',
      },
      {
        icon: 'legend_toggle',
        label: 'Icon-Legende',
        description: 'Bedeutung aller Icons und Status-Symbole im System.',
      },
      {
        icon: 'menu_book',
        label: 'Orga-Doku / Admin-Doku',
        description: 'Dokumentation für Organisatoren und Administratoren.',
      },
      {
        icon: 'logout',
        label: 'Ausloggen',
        description: 'Aus dem System ausloggen.',
        accent: 'negative',
      },
    ],
  },
];

const UPCOMING_EVENTS_SECTIONS = [
  {
    title: 'Allgemein',
    items: [
      {
        icon: 'note_stack',
        label: 'Memo',
        description: 'Für diesen Spieltag wurde eine interne Notiz hinterlegt.',
        accent: 'accent',
      },
      {
        icon: 'lock_person',
        label: 'Anmeldung geschlossen',
        description: 'Privater Spieltag mit geschlossener Anmeldung – keine neuen Anmeldungen über Einladungslink und persönliche Links. Admins öffnen sie wieder über das Kontextmenü („Anmeldung öffnen“).',
        accent: 'warning',
      },
    ],
  },
  {
    title: 'Spieltag-Status',
    items: [
      {
        element: <EventStatus statusId="Draft" single compact />,
        label: 'Entwurf',
        description: 'Spieltag ist angelegt, aber noch für keine Gruppe freigegeben.',
      },
      {
        element: <EventStatus statusId="MemberPreview" single compact />,
        label: 'Member-Vorschau',
        description: 'Für einzelne Gruppen freigegeben (Tooltip „Freigegeben für …“, z. B. Mitglieder) — sie melden sich über ihren persönlichen Link an. Öffentlich ist der Spieltag noch nicht.',
      },
      {
        element: <EventStatus statusId="Published" single compact />,
        label: 'Veröffentlicht',
        description: 'Für Gäste freigegeben — der Spieltag erscheint öffentlich auf der Homepage und gilt für alle Gruppen.',
      },
      {
        element: <EventStatus statusId="Completed" single compact />,
        label: 'Abgeschlossen',
        description: 'Alle Teilnehmer haben einen Anwesenheitsstatus — der Spieltag ist final ausgewertet.',
      },
      {
        element: <EventStatus statusId="Cancelled" single compact />,
        label: 'Abgesagt',
        description: 'Der Spieltag wurde abgesagt — die Progression ist abgebrochen.',
      },
    ],
  },
  {
    // Never-public events with invitation link: own stages, never "Veröffentlicht" (EventStatus STAGES_PRIVATE).
    title: 'Status privater Events',
    items: [
      {
        element: <EventStatus statusId="Draft" neverPublic linkSignup single compact />,
        label: 'In Bearbeitung',
        description: 'Privates Event ist angelegt — Gäste melden sich über den Einladungslink an.',
      },
      {
        element: <EventStatus statusId="MemberPreview" neverPublic linkSignup single compact />,
        label: 'Für Member freigegeben',
        description: 'Für Gruppen mit persönlichem Link freigegeben (z. B. Mitglieder) — das Event steht auf ihrer persönlichen Seite, sie melden sich dort selbst an.',
      },
      {
        element: <EventStatus statusId="Completed" neverPublic linkSignup single compact />,
        label: 'Abgeschlossen',
        description: 'Alle Teilnehmer haben einen Anwesenheitsstatus. Private Events werden nie veröffentlicht.',
      },
    ],
  },
  {
    title: 'Spieltag-Art',
    items: [
      {
        icon: 'public',
        label: 'Öffentlich',
        description: 'Der Spieltag ist öffentlich.',
        accent: 'accent',
      },
      {
        icon: 'lock',
        label: 'Privat',
        description: 'Nicht öffentlich gelistet – Gäste melden sich nur über den Einladungslink an, freigegebene Gruppen (z. B. Mitglieder) über ihren persönlichen Link.',
        accent: 'negative',
      },
      {
        icon: 'groups',
        label: 'Feldmiete',
        description: 'Nie öffentlich, ohne Einladungslink (z. B. Feldmiete) — der Tooltip nennt die Art aus der Prozessvorlage.',
        accent: 'warning',
      },
    ],
  },
];

const EVENT_SECTIONS = [
  {
    title: 'Allgemein',
    items: [
      {
        icon: 'sports',
        label: 'Spieltagleitung',
        description: 'Der Spieler leitet diesen Spieltag.',
        accent: 'negative',
      },
      {
        icon: 'note_stack',
        label: 'Spieltag-Memo',
        description: 'Für diesen Spieler an diesem Spieltag gibt es eine Notiz.',
        accent: 'accent',
      },
      {
        icon: 'note_stack',
        label: 'Spieler-Memo',
        description: 'Im Spielerprofil ist dauerhaft eine Notiz hinterlegt.',
        accent: 'warning',
      },
      {
        icon: 'send',
        label: 'E-Mail ausstehend',
        description: 'Für die Gruppe des Spielers ist eine Statusmail vorgesehen, sie wurde aber noch nicht versendet.',
        accent: 'negative',
      },
      {
        icon: 'visibility_off',
        label: 'Spieler anonymisiert',
        description: 'Die personenbezogenen Spielerdaten wurden gelöscht.',
        accent: 'negative',
      },
    ],
  },
  {
    title: 'Warteliste',
    items: [
      {
        element: <WaitlistStatusIcon sentAt={null} />,
        label: 'Warteliste — keine Mail',
        description: 'Spieler steht auf der Warteliste, es wurde noch keine Platz-frei-Mail gesendet.',
      },
      {
        element: <WaitlistStatusIcon sentAt={new Date(_now - 1000)} />,
        label: 'Warteliste — Mail gerade gesendet',
        description: 'Platz-frei-Mail wurde eben versendet, 48h-Frist hat begonnen. Spieler hat noch ~48h Zeit zu antworten.',
      },
      {
        element: <WaitlistStatusIcon sentAt={new Date(_now - 8 * 3600 * 1000)} />,
        label: 'Warteliste — Mail vor 8 Stunden',
        description: 'Mail wurde vor 8 Stunden gesendet, Frist läuft noch (unter 24h).',
      },
      {
        element: <WaitlistStatusIcon sentAt={new Date(_now - 32 * 3600 * 1000)} />,
        label: 'Warteliste — Mail vor 32 Stunden',
        description: 'Mehr als 24h seit Mailversand vergangen, Frist läuft noch ab (unter 48h). Farbe wechselt zu Gelb.',
      },
      {
        element: <WaitlistStatusIcon sentAt={new Date(_now - 49 * 3600 * 1000)} />,
        label: 'Warteliste — Frist abgelaufen',
        description: 'Die 48-Stunden-Frist ist abgelaufen, ohne dass der Spieler geantwortet hat.',
      },
    ],
  },
  {
    title: 'Anwesenheit',
    items: [
      {
        element: <Icon className="playerPresent checkbox present" icon="done" />,
        label: 'Anwesend',
        description: 'Der Spieler ist anwesend.',
      },
      {
        element: <Icon className="playerPresent checkbox notPresent" icon="close" />,
        label: 'Abwesend',
        description: 'Der Spieler ist abwesend.',
      },
      {
        element: <Icon className="playerPresent checkbox" icon="radio_button_unchecked" />,
        label: 'Nicht definiert',
        description: 'Der Spieler hat keinen Anwesenheitsstatus.',
      },
    ],
  },
  {
    title: 'Bezahlung',
    items: [
      {
        element: <span className="legend-price"><span className="material-symbols-rounded legend-pay-icon legend-pay-icon--paid">check</span>15,00 €</span>,
        wide: true,
        label: 'Bezahlt',
        description: 'Grünes Häkchen vor dem Preis: als bezahlt markiert — automatisch beim Check-in (wenn am Spieltag eingeschaltet) oder manuell über das Kontextmenü.',
      },
      {
        element: <span className="legend-price"><span className="material-symbols-rounded legend-pay-icon legend-pay-icon--open">close</span>15,00 €</span>,
        wide: true,
        label: 'Offen',
        description: 'Rotes X vor dem Preis: der Spieler muss zahlen und hat noch nicht bezahlt.',
      },
      {
        element: <span className="legend-price">15,00 €</span>,
        wide: true,
        label: 'Nichts offen',
        description: 'Preis ohne Symbol: nichts zu zahlen (z. B. nur Artikel) oder abwesend und unbezahlt (No-Show) — zählt nicht als offen.',
      },
      {
        element: <span className="legend-price">15,00 €<span className="material-symbols-rounded legend-price-override-icon">edit</span></span>,
        wide: true,
        label: 'Manueller Preis',
        description: 'Stift hinter dem Preis: manuell gesetzt, wird nicht mehr automatisch neu berechnet. Admins setzen ihn im Kontextmenü mit „Standardpreis wiederherstellen“ zurück.',
      },
      {
        // Als Element (statt icon), damit die Beschriftung mit den Preis-Beispielen fluchtet.
        element: <span className="material-symbols-rounded legend-icon legend-icon--negative">payments</span>,
        wide: true,
        label: 'Chip „offen“',
        description: 'Oben in der Liste: Anzahl und Summe der offenen Zahlungen (ohne Warteliste und No-Shows).',
      },
    ],
  },
  {
    // Add-ons ("Zusätze"): same chip classes as AddOnChips (spans only, the legend slot is a span).
    title: 'Zusätze',
    items: [
      {
        element: <AddOnChipSample label="BBs ×2" />,
        chip: true,
        label: 'Gebuchter Zusatz',
        description: 'Unter dem Preis: bei der Anmeldung gebuchte Zusätze mit Menge, z. B. „Leihausrüstung“ oder „BBs ×2“ — Teil des Preises des Spielers.',
      },
      {
        element: <AddOnChipSample label="BBs 5/8" />,
        chip: true,
        label: 'Zähler je Zusatz',
        description: 'Oben in der Liste (auch in der Spieltage-Liste, auf der Startseite und in den Anmeldungen): gebuchte Menge je Anmelde-Zusatz des Prozesses / Höchstmenge — nur die Zahl, wenn unbegrenzt. Zählt Anmeldungen und Artikel, ohne Warteliste.',
      },
      {
        element: <AddOnChipSample label="BBs 8/8" warning />,
        chip: true,
        label: 'Zusatz ausgebucht',
        description: 'Höchstmenge erreicht — je nach Zusatz kommen neue Anmeldungen damit auf die Zusatz-Warteliste oder werden ohne ihn angenommen.',
      },
      {
        element: <span className="material-symbols-rounded legend-icon legend-icon--accent">shopping_cart</span>,
        chip: true,
        label: 'Artikel gebucht',
        description: 'Auf diesen Spieler sind im Event Artikel gebucht (z. B. BBs) — vor Ort bezahlt, nicht Teil seines Preises.',
      },
    ],
  },
];

// Team color (EventPlayer.group) — formerly "Gruppe"; "Gruppe" now means the participant group.
const TEAM_SUBSECTION = {
  title: 'Team',
  items: [1, 2, 3, 'X'].map(n => ({
    element: <svg className={`listColorExtensionItem group${n}`} width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 2 V 10 A 9 9 0 0 0 11 19 H 19 L 2 2 Z"/></svg>,
    label: `Team ${n}`,
    description: `Der Spieler ist in Team ${n}.`,
  })),
};

const USERS_SECTIONS = [
  {
    title: 'Allgemein',
    items: [
      {
        icon: 'note_stack',
        label: 'Spieler-Memo',
        description: 'Im Spielerprofil ist dauerhaft eine Notiz hinterlegt.',
        accent: 'warning',
      },
    ],
  },
];

const MEMBERS_SECTIONS = [
  {
    title: 'Allgemein',
    items: [
      {
        icon: 'visibility_off',
        label: 'Spieler anonymisiert',
        description: 'Die personenbezogenen Spielerdaten wurden gelöscht.',
        accent: 'negative',
      },
    ],
  },
];

// Shown until the group list is loaded (or when it cannot be loaded): the system group guest.
const FALLBACK_GROUPS = normalizeParticipantGroups([
  { id: -2, name: 'Gast', colorKey: 'neutral', systemKey: GUEST_SYSTEM_KEY, isGuest: true, sortOrder: 1000 },
]);

// "Gruppe & Rolle": one example dot per group (real colors from the configuration) plus the role colors
// that override the group color. roleSettings: configured settings (/groups › Rollen); while they are not
// loaded, roleLabel/roleColorKey return the seeded defaults (Orga red, Admin orange).
function personDotSubsection(groups, roleSettings) {
  const roles = roleSettings.length > 0 ? roleSettings.map(r => r.role) : ROLE_ORDER;
  const groupItems = groups.map(g => ({
    element: <ParticipantGroupDot participantGroup={g} withTooltip={false} />,
    label: g.name,
    description: (g.isGuest ? 'Spieler ohne Gruppenzugehörigkeit.' : `Der Spieler ist in der Gruppe „${g.name}“.`)
      + (g.colorKey === 'neutral' ? ' Leerer Ring: Gruppe ohne Farbe.' : ''),
  }));
  const roleItems = roles.map(role => ({
    element: <ParticipantGroupDot role={role} withTooltip={false} />,
    label: roleLabel(role),
    description: `Rolle ${roleLabel(role)} — überschreibt die Gruppenfarbe. Leitet den Spieltag nicht zwingend.`,
  }));
  const roleColors = roles
    .map(role => `${roleLabel(role)} (${COLOR_KEY_LABELS[roleColorKey(role)] ?? roleColorKey(role)})`)
    .join(', ');
  return {
    title: 'Gruppe & Rolle',
    hint: `Farbe der Gruppe (in der Gruppenkonfiguration gewählt); die Rollen ${roleColors} überschreiben sie.`,
    items: [...groupItems, ...roleItems],
  };
}

const WIKI_SECTIONS = [
  {
    title: 'Einträge',
    items: [
      {
        icon: 'priority_high',
        label: 'Wichtig',
        description: 'Der Eintrag ist wichtig.',
        accent: 'negative',
      },
    ],
  },
];

function LegendIcon({ icon, element, accent, wide = false, chip = false }) {
  if (element) {
    const slot = chip ? ' legend-component-icon--chip' : wide ? ' legend-component-icon--wide' : '';
    return (
      <span className={`legend-component-icon${slot}`}>
        {element}
      </span>
    );
  }

  return (
    <span className={`material-symbols-rounded legend-icon ${accent ? `legend-icon--${accent}` : ''}`}>
      {icon}
    </span>
  );
}

function LegendItem({ item }) {
  return (
    <li className="legend-item">
      <LegendIcon
        icon={item.icon}
        element={item.element}
        accent={item.accent}
        wide={item.wide}
        chip={item.chip}
      />

      <div className="legend-item-text">
        <span className="legend-item-label">{item.label}</span>
        <span className="legend-item-desc">{item.description}</span>
      </div>
    </li>
  );
}

function LegendSubSection({ title, hint, items }) {
  return (
    <div className="legend-subsection">
      <h5 className="legend-subsection-title">{title}</h5>
      {hint && <p className="legend-subsection-hint">{hint}</p>}

      <ul className="legend-list">
        {items.map((item, i) => (
          <LegendItem key={i} item={item} />
        ))}
      </ul>
    </div>
  );
}

function LegendSection({ title, subsections }) {
  return (
    <div className="legend-section">
      <h4 className="legend-section-title">{title}</h4>

      <div className="legend-subsection-list">
        {subsections.map((subsection, i) => (
          <LegendSubSection
            key={i}
            title={subsection.title}
            hint={subsection.hint}
            items={subsection.items}
          />
        ))}
      </div>
    </div>
  );
}

// Rendered only while the legend is open, so the group list is requested on demand (shared hook).
function LegendBody() {
  const { active, loaded } = useParticipantGroups();
  // Configured role settings (empty until loaded → seeded defaults).
  const { roles } = useRoleSettings();
  const groups = loaded && active.length > 0 ? active : FALLBACK_GROUPS;

  const eventSections = useMemo(
    () => [...EVENT_SECTIONS, personDotSubsection(groups, roles), TEAM_SUBSECTION],
    [groups, roles],
  );
  const userSections = useMemo(
    () => [...USERS_SECTIONS, personDotSubsection(groups, roles)],
    [groups, roles],
  );
  // The group tab lists the members of one group — guests never appear there.
  const memberSections = useMemo(
    () => [...MEMBERS_SECTIONS, personDotSubsection(groups.filter(g => !g.isGuest), roles)],
    [groups, roles],
  );

  return (
    <div className="legend-popup-body">
      <LegendSection
        title="Navigation"
        subsections={NAV_SECTIONS}
      />

      <LegendSection
        title="Spieltage (/upcoming-events)"
        subsections={UPCOMING_EVENTS_SECTIONS}
      />

      <LegendSection
        title="Spieltag-Ansicht (/event)"
        subsections={eventSections}
      />

      <LegendSection
        title="Benutzer-Liste (/users)"
        subsections={userSections}
      />

      <LegendSection
        title="Spieler › Tab „Gruppen“ (/users)"
        subsections={memberSections}
      />

      <LegendSection
        title="Wiki-Seite"
        subsections={WIKI_SECTIONS}
      />
    </div>
  );
}

export default function IconLegendPopup() {
  const { legendOpen, closeLegend } = useTour();

  return (
    <AnimatePresence>
      {legendOpen && (
        <>
          <motion.div
            className="legend-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLegend}
          />

          <motion.div
            className="legend-popup-shell"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="legend-popup"
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
            >
              <div className="legend-popup-header">
                <h3 className="legend-popup-title">Icon-Legende</h3>

                <button
                  type="button"
                  className="legend-close-btn"
                  onClick={closeLegend}
                  aria-label="Legende schließen"
                >
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>

              <LegendBody />
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}